import type { BoardTile } from '../../../engine/board.js';

/**
 * Pyramid layout — 144-tile stepped pyramid. Four layers, each with a
 * smaller rectangular footprint than the one below, all centered on the
 * origin. Each layer is offset half a cell from the one below it on both
 * axes so layer N's tiles straddle the gaps between layer N-1's tiles —
 * the same staggering pattern Turtle uses for its 6×6 / 4×4 / 2×2 stacks.
 *
 *   Layer 0 (72)  9 × 8 base
 *   Layer 1 (48)  8 × 6 (offset by -0.5 in x, -1 in y so it nests inside)
 *   Layer 2 (20)  5 × 4
 *   Layer 3  (4)  2 × 2 cap
 */

function rectLayer(
  cols: number,
  rows: number,
  cx: number,
  cy: number,
  layer: number,
): Omit<BoardTile, 'id'>[] {
  const out: Omit<BoardTile, 'id'>[] = [];
  const x0 = cx - (cols - 1) / 2;
  const y0 = cy - (rows - 1) / 2;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      out.push({ x: x0 + c, y: y0 + r, layer });
    }
  }
  return out;
}

export const pyramidLayout: ReadonlyArray<Omit<BoardTile, 'id'>> = (() => {
  const out: Omit<BoardTile, 'id'>[] = [];
  // L0: 9 × 8 = 72  (centered on origin → x ∈ -4..4, y ∈ -3.5..3.5)
  out.push(...rectLayer(9, 8, 0, 0, 0));
  // L1: 8 × 6 = 48  (offset half a cell in x; integer-cell y centered)
  out.push(...rectLayer(8, 6, 0, 0, 1));
  // L2: 5 × 4 = 20
  out.push(...rectLayer(5, 4, 0, 0, 2));
  // L3:  2 × 2 cap = 4
  out.push(...rectLayer(2, 2, 0, 0, 3));

  if (out.length !== 144) {
    throw new Error(`pyramid layout has ${out.length} positions, expected 144`);
  }
  return out;
})();
